import AOS from "aos";
import { useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Accordion } from "../../components/accordion";
import { Animate } from "../../components/animate";
import { Button } from "../../components/button";
import { CTA } from "../../components/cards/cta";
import { InfoCard } from "../../components/cards/InfoCard";
import { GetStarted } from "../../components/common/getStarted";
import { HeroImage } from "../../components/common/heroImage";
import { Layout } from "../../components/layout";
import { ContactForm } from "../../components/forms/ContactForm";
import { LoanSection } from "../../components/loans";
import { BuzzSection } from "../../components/buzz";
// import { NewsSection } from "../../components/news/NewsSection";
import { APPSTORE_URL, PLAYSTORE_URL } from "../../constants";

const cardDetails = [
  {
    imgSrc: "/assets/images/autoSavings.svg",
    title: (
      <p>
        Automated
        <br />
        Savings
      </p>
    ),
    subtitle: (
      <>
        As you spend with your debit card(s), we help you save. Just specify the
        amount and we will save it for you with an interest rate from{" "}
        <span className="text-grey-800 font-bold">10.5%</span> per annum."
      </>
    ),
    className: "bg-primary/5",
    textClassName: "text-deep-primary",
  },
  {
    imgSrc: "/assets/images/flexSavings.svg",
    title: (
      <p>
        Flexible
        <br />
        Savings
      </p>
    ),
    subtitle: (
      <>
        Save, transfer, withdraw, manage and organize your money for free at any
        time with an interest rate from{" "}
        <span className="text-grey-800 font-bold">12%</span> per annum.
      </>
    ),
    className: "bg-pink/5",
    textClassName: "text-deep-pink",
  },
  {
    imgSrc: "/assets/images/fixedSavings.svg",
    title: (
      <p>
        Fixed
        <br />
        Savings
      </p>
    ),
    subtitle: (
      <>
        Lock money away for a fixed duration until maturity. You can get a cash
        advance in time of need with an interest rate from{" "}
        <span className="text-grey-800 font-bold">18%</span> to{" "}
        <span className="text-grey-800 font-bold">20%</span> per annum.
      </>
    ),
    className: "bg-green/5",
    textClassName: "text-deep-green",
  },
];

const steps = [
  {
    title: "Download the App",
    text: "Click on the MYCARD NG App from the store of your mobile.",
  },
  {
    title: "Create an account",
    text: "Seamlessly create an account within the app.",
  },
  {
    title: "Explore our features",
    text: "Explore every features we have from savings to loans to cards and to buzz",
  },
];

const faqs = [
  {
    question: "How safe is MyCardGlobal?",
    answer:
      "MyCardGlobal is designed with security and customer protection in mind. We do not hold customer funds. Payments are processed through Paystack, while savings funds are held by Paystack MFB, a licensed financial institution. MyCard uses secure integrations and industry-standard security practices to protect customer information and transactions.",
  },
  {
    question: "What does MyCardGlobal do?",
    answer:
      "MyCardGlobal is a digital fintech platform that helps you save, access credit and manage your money smarter. Our products include Autosave, Flexisave, Vault, Loans and Buzz, giving customers convenient tools to build better financial habits and achieve their financial goals.",
  },
  {
    question: "Where can I get the MyCardGlobal app?",
    answer: (
      <>
        You can download the MyCardGlobal app from the Google Play Store or
        Apple App Store, subject to availability. You can also visit{" "}
        <a href="www.mycardglobal.com#faq">www.mycardglobal.com</a> for more
        information and download options.
      </>
    ),
  },
  {
    question: "Will there be a maintenance fee for a MyCardGlobal card?",
    answer:
      "There is currently no maintenance fee for the MyCard card. Any applicable charges or fees will always be clearly communicated to customers before they use a chargeable service.",
  },
];

const accredittedImages = [
  {
    src: "/assets/images/Lagos_Home_affairs_small.jpg",
    alt: "Lagos State Government Ministry of Home Affairs",
  },
  {
    src: "/assets/images/NDPC-logo-copped-compressed.jpg",
    alt: "Nigeria Data Protection Commission (NDPC)",
  },
  {
    src: "/assets/images/SCUML-logo-compressed.jpg",
    alt: "Special Control Unit against Money Laundering (SCUML)",
  },
  {
    src: "/assets/images/paystack_MFB_logo_cropped.png",
    alt: "Paystack Microfinance bank",
  },
];

export const LandingPage = () => {
  const [openAccordion, setOpenAccordion] = useState();

  const heroRef = useRef();
  const heroTextRef = useRef();

  const isHeroInView = useInView(heroRef);

  useEffect(() => {
    AOS.init({
      once: true,
    });

    setTimeout(() => {
      AOS.refresh();
    }, 1000);
  }, []);

  return (
    <Layout
      navBarClassName={`${
        isHeroInView ? "!bg-light-yellow !border-yellow/20" : ""
      }`}
    >
      <div className="w-full">
        {/* Hero section  */}
        <div
          ref={heroRef}
          className="bg-light-yellow relative pt-[64px] xl:pt-[100px] flex flex-col xl:flex-row justify-center"
        >
          <div ref={heroTextRef} className="max-width xl:absolute z-10">
            <div className="w-full max-w-[762px] flex flex-col gap-[18px]">
              <h1
                data-aos="fade-down"
                data-aos-duration="1000"
                className="max-w-[680px] tracking-[-0.04em] text-deep-primary text-[36px] leading-[40px] sm:text-[56px] sm:leading-[56px] xl:text-[80px] xl:leading-[90px] font-medium"
              >
                Your money,{" "}
                <span className="italic text-primary">made easier</span>
              </h1>

              <p
                data-aos="fade-up"
                data-aos-duration="1000"
                className="max-w-[620px] font-medium text-base leading-6 text-grey-800 sm:text-lg sm:leading-7 md:text-2xl md:leading-[32px] "
              >
                Save automatically, manage your cards, track your money, and
                take control of your everyday finances all in one place.
              </p>

              <div className="flex items-center gap-4 flex-wrap md:mt-[14px] xl:mt-[36px]">
                <Button
                  className="max-w-fit xl:!text-lg !xl:px-[20px] !xl:py-[20px]"
                  text={"GET ON ANDROID"}
                  icon="playstore-colored"
                  variation="dark-grey"
                  onClick={() => {
                    window.location.href = PLAYSTORE_URL;
                  }}
                />

                <Button
                  className="max-w-fit xl:!text-lg !xl:px-[20px] !xl:py-[20px]"
                  text={"GET ON APPLE"}
                  icon="apple"
                  variation="dark-grey"
                  onClick={() => {
                    window.location.href = APPSTORE_URL;
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className="w-full flex justify-center md:justify-end items-end !min-h-fit !max-h-fit xl:!max-h-max"
            style={{
              minHeight: `${heroTextRef?.current?.offsetHeight ?? 0}px`,
            }}
            data-aos="fade-up"
            data-aos-duration="500"
          >
            <HeroImage />
          </div>
        </div>

        <div className="accreditted w-full py-10 md:py-20 bg-white">
          <Animate animation="fade-up">
            <div className="max-width flex flex-col items-center gap-6 md:gap-20">
              <h2 className="text-center text-deep-primary font-bold text-xl md:text-2xl lg:text-[26px] tracking-[-0.02em]">
                Accredited & Supported by :
              </h2>

              <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-6 lg:gap-8 max-w-[1100px]">
                {accredittedImages.map(({ src, alt }) => (
                  <div
                    key={src}
                    className="flex items-center justify-center p-6 sm:p-8 bg-white border border-grey-200 rounded-[20px] h-[150px] md:h-[165px] lg:h-[180px] shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <img
                      src={src}
                      alt={alt}
                      className="max-h-[100px] md:max-h-[110px] w-auto max-w-[85%] object-contain"
                    />
                  </div>
                ))}
              </div>
            </div>
          </Animate>
        </div>

        <div className="more-details">
          <div
            id="more-details"
            className="flex justify-center more-details w-full py-[56px] lg:py-[80px]"
          >
            <div className="w-full max-w-[1090px] flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
              <Animate animation="fade-up">
                <div className="w-full lg:max-w-[560px] flex flex-col gap-6">
                  <h2 className="text-deep-primary font-medium text-[36px] leading-[40px] sm:text-[48px] sm:leading-[54px] lg:text-[64px] lg:leading-[60px] tracking-[-0.02em]">
                    Your money <br />
                    deserves protection.
                  </h2>

                  <p className="text-grey-600 font-normal text-base sm:text-lg lg:text-xl leading-[24px] sm:leading-[28px] lg:leading-[32px] max-w-[480px]">
                    From your personal information to your financial
                    transactions, MyCardNG is designed with security and privacy
                    in mind.
                  </p>

                  <div className="pt-2">
                    <a
                      href="/privacy-policy"
                      className="inline-flex items-center gap-2 text-primary font-semibold text-base sm:text-xl sm:leading-6 hover:gap-3 transition-all duration-300 group"
                    >
                      <span>Learn more about our security</span>
                      <span className="text-xl transition-transform group-hover:translate-x-1">
                        →
                      </span>
                    </a>
                  </div>
                </div>
              </Animate>

              <Animate animation="fade-down" delay={0.2}>
                <div className="overflow-hidden relative w-full sm:w-[405px] bg-[#B65C080D] rounded-[32px] p-8 sm:p-10 lg:px-6 lg:pt-[19px] lg:pb-[34px] flex flex-col">
                  <div
                    style={{
                      width: "139%",
                      top: "-92.5%",
                      height: "27rem",
                      right: "-18.7%",
                      borderBottomRightRadius: "100%",
                      borderBottomLeftRadius: "100%",
                    }}
                    className="absolute bg-[#B65C080D]"
                  />
                  <div className="relative flex items-center justify-center mb-4 overflow-hidden">
                    <img
                      src="assets/images/secure-lock.svg"
                      alt="secure lock"
                    />
                  </div>

                  <h3 className="text-deep-primary font-bold text-[28px] leading-[34px] lg:text-[26px] lg:leading-[32px] mb-3">
                    Security first
                  </h3>
                  <p className="text-grey-600 text-sm lg:text-lg lg:leading-[18px] mt-1 mb-6">
                    Your information deserves protection.
                  </p>
                  <div className="flex flex-col gap-5 py-[10px]">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-main-black flex items-center justify-center text-white flex-shrink-0">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2.5 6L5 8.5L9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="text-grey-900 font-medium text-sm sm:text-base">
                        Data Protection
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-main-black flex items-center justify-center text-white flex-shrink-0">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2.5 6L5 8.5L9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="text-grey-900 font-medium text-sm sm:text-base">
                        Account Verification
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-main-black flex items-center justify-center text-white flex-shrink-0">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path
                            d="M2.5 6L5 8.5L9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="text-grey-900 font-medium text-sm sm:text-base">
                        Secure Transactions
                      </span>
                    </div>
                  </div>
                </div>
              </Animate>
            </div>
          </div>
        </div>

        <div className="w-full bg-grey-50 py-[56px] lg:py-[80px]">
          <div className="max-width flex flex-col justify-center items-center gap-[42px]">
            <Animate animation="fade-down" delay={0.25}>
              <p className="text-deep-primary text-center max-w-fit text-[30px] leading-[36px] lg:text-[64px] lg:leading-[60px] font-medium tracking-[-0.02em]">
                Get started{" "}
                <span className="italic text-primary font-normal">
                  with <br />
                  Ease
                </span>
              </p>
            </Animate>

            <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-8">
              <Animate animation="fade-up">
                <div className="w-full max-w-[546px] flex flex-col gap-8">
                  {steps.map((step, idx) => {
                    return (
                      <div className="flex gap-4" key={`${idx}`}>
                        <p className="min-w-[32px] lg:min-w-[52px] h-[32px] lg:h-[52px] rounded-full bg-primary/5 flex items-center justify-center font-bold text-xs lg:text-lg">
                          0{idx + 1}
                        </p>

                        <div className="flex-1 flex flex-col gap-1">
                          <p className="text-2xl lg:text-[30px] text-deep-primary font-medium">
                            {step.title}
                          </p>
                          <p className="text-grey-800 text-sm lg:text-lg leading-[20px] lg:leading-[28px]">
                            {step.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Animate>
              <GetStarted className="block w-full max-w-[624px] order-first lg:order-last" />
            </div>
          </div>
        </div>

        <div className="w-full py-[80px] flex flex-col">
          <div id="savings" className="max-width flex flex-col gap-[84px]">
            <Animate animation="fade-down">
              <div className="gap-8 flex flex-col lg:flex-row justify-between items-center">
                <div className="w-full lg:max-w-[525px]">
                  <p className="text-deep-primary font-medium text-[40px] leading-[44px] sm:text-[48px] sm:leading-[52px] lg:text-[64px] lg:leading-[60px] tracking-[-0.02em]">
                    Experience flexible ways to save
                  </p>

                  <p className="mt-6 text-base lg:text-lg font-medium text-grey-800">
                    Earn 5%-15% when you save with any of these MyCardGlobal
                    savings plans.
                  </p>
                </div>

                <div className="flex gap-4 lg:gap-8 w-full lg:max-w-fit">
                  <div>
                    <p className="text-grey-600 text-xs lg:text-sm font-medium">
                      EXPLORE OVER
                    </p>

                    <p>
                      <span className="text-primary font-bold text-[36px] lg:text-[56px]">
                        3+
                      </span>
                      <span className="pb-[10px] text-grey-900 font-medium text-sm md:text-base">
                        {"  "}
                        Saving types
                      </span>
                    </p>
                  </div>

                  <div className="border-l border-grey-300 pl-4 lg:pl-8">
                    <p className="text-grey-600 text-xs lg:text-sm font-medium">
                      EARN OVER
                    </p>

                    <p>
                      <span className="text-primary font-bold text-[36px] lg:text-[56px]">
                        9%
                      </span>
                      <span className="pb-[10px] text-grey-900 font-medium text-sm md:text-base">
                        {"  "}
                        in interests
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </Animate>

            <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-8 mb-6">
              {cardDetails.map((cd, idx) => {
                return (
                  <Animate key={`${idx}`} animation="fade-up" delay={idx / 4}>
                    <InfoCard {...cd} />
                  </Animate>
                );
              })}
            </div>
          </div>
        </div>

        {/* Loan Products Section */}
        <LoanSection />

        {/* Buzz Section */}
        <BuzzSection />

        {/* News Section */}
        {/* <NewsSection /> */}

        <CTA />
        {/* Don't render the FAQ for now */}
        {false && (
          <div className="py-[56px] lg:py-[80px]" id="faqs">
            <div className="max-width flex flex-col lg:flex-row gap-8 justify-between">
              <div className="flex flex-col gap-4">
                <p className="text-deep-primary font-medium text-[56px] lg:text-[64px] tracking-[-0.02em]">
                  FAQs
                </p>
                <p className="font-medium text-grey-500 text-sm md:text-base lg:text-lg">
                  Have some questions? <br />
                  We've probably already answered them.
                </p>
              </div>

              <div className="flex flex-col gap-4 w-full max-w-[733px]">
                {/* Savings FAQs */}
                {faqs.map((faq, idx) => {
                  return (
                    <Accordion
                      mainText={faq.question}
                      subText={faq.answer}
                      key={`savings-${idx}`}
                      isOpen={openAccordion === `savings-${idx}`}
                      onOpen={() => {
                        if (openAccordion === `savings-${idx}`) {
                          setOpenAccordion(undefined);
                        } else {
                          setOpenAccordion(`savings-${idx}`);
                        }
                      }}
                    />
                  );
                })}

                {/* Loan FAQs */}
                {/* {loanFaqs.map((faq, idx) => {
                return (
                  <Accordion
                    mainText={faq.question}
                    subText={faq.answer}
                    key={`loan-${idx}`}
                    isOpen={openAccordion === `loan-${idx}`}
                    onOpen={() => {
                      if (openAccordion === `loan-${idx}`) {
                        setOpenAccordion(undefined);
                      } else {
                        setOpenAccordion(`loan-${idx}`);
                      }
                    }}
                  />
                );
              })} */}

                {/* Buzz FAQs */}
                {/* {buzzFaqs.map((faq, idx) => {
                return (
                  <Accordion
                    mainText={faq.question}
                    subText={faq.answer}
                    key={`buzz-${idx}`}
                    isOpen={openAccordion === `buzz-${idx}`}
                    onOpen={() => {
                      if (openAccordion === `buzz-${idx}`) {
                        setOpenAccordion(undefined);
                      } else {
                        setOpenAccordion(`buzz-${idx}`);
                      }
                    }}
                  />
                );
              })} */}
              </div>
            </div>
          </div>
        )}

        <ContactForm />
      </div>
    </Layout>
  );
};
